import { asUTCDate } from '@shared/utils/DateTime';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgButton, AgModal, AgModalActions, AgModalContent, AgText } from 'src/components/ui';
import { notify, toUserMessage } from 'src/lib/notify';
import { summarizeMergePreview } from './duplicateMergePreview';
import {
  type DuplicateBusinessEntityMatch,
  type DuplicateBusinessEntityMember,
  type DuplicateBusinessEntityRole,
  useDuplicateBusinessEntities,
} from './hooks/useDuplicateBusinessEntities';

type Props = { open: boolean; onClose: () => void; onChanged: () => void };

const matchLabels: Record<DuplicateBusinessEntityMatch, string> = {
  NAME: 'same name',
  BLUE_BOOK_ID: 'same Blue Book ID',
  DUNS_NUMBER: 'same DUNS number',
  ADDRESS_AND_SIMILAR_NAME: 'same address and similar name',
};

const counted = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** What a reviewer recognizes a record by: place, activity and recency — never internal ids. */
function memberFacts(member: DuplicateBusinessEntityMember) {
  const lastOrder = asUTCDate(member.lastOrderDate)?.toFormat('LLL d, yyyy');
  return [
    [member.city, member.state].filter(Boolean).join(', '),
    counted(member.orderCount, 'order'),
    lastOrder ? `last order ${lastOrder}` : '',
    counted(member.billCount, 'bill'),
    counted(member.contactCount, 'contact'),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** A guarded review flow: choose a role and survivor, inspect moved-row counts, then merge. */
export default function DuplicateBusinessEntitiesModal({ open, onClose, onChanged }: Props) {
  const [role, setRole] = useState<DuplicateBusinessEntityRole>('CUSTOMER');
  const [groupKey, setGroupKey] = useState<string | null>(null);
  const [survivorId, setSurvivorId] = useState<number | null>(null);
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [mergedRecordId, setMergedRecordId] = useState<number | null>(null);
  const [searchSyncPending, setSearchSyncPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const previewAttempt = useRef(0);
  const {
    groups,
    previewResult,
    find,
    preview,
    merge: runMerge,
    undo: runUndo,
    finding,
    previewing,
    merging,
    undoing,
  } = useDuplicateBusinessEntities();

  const findForRole = useCallback(
    async (nextRole: DuplicateBusinessEntityRole) => {
      previewAttempt.current += 1;
      setRole(nextRole);
      setGroupKey(null);
      setSurvivorId(null);
      setIsPreviewReady(false);
      setMergedRecordId(null);
      setSearchSyncPending(false);
      setFailure(null);
      try {
        await find(nextRole);
      } catch (error) {
        setFailure(`Could not find duplicates. ${toUserMessage(error)}`);
      }
    },
    [find]
  );

  useEffect(() => {
    if (open) void findForRole('CUSTOMER');
  }, [findForRole, open]);

  const selectedGroup = failure ? undefined : (groups.find((group) => group.key === groupKey) ?? groups[0]);
  const duplicateIds = useMemo(
    () => selectedGroup?.members.map(({ entity }) => entity.id).filter((id) => id !== survivorId) ?? [],
    [selectedGroup, survivorId]
  );
  const previewSummary = summarizeMergePreview(previewResult?.referenceCounts ?? []);

  const selectGroup = useCallback((key: string) => {
    previewAttempt.current += 1;
    setGroupKey(key);
    setSurvivorId(null);
    setIsPreviewReady(false);
    setMergedRecordId(null);
    setSearchSyncPending(false);
    setFailure(null);
  }, []);

  const selectSurvivor = useCallback((id: number) => {
    previewAttempt.current += 1;
    setSurvivorId(id);
    setIsPreviewReady(false);
    setFailure(null);
  }, []);

  const previewMerge = useCallback(async () => {
    if (survivorId === null || duplicateIds.length === 0) return;
    const attempt = ++previewAttempt.current;
    setFailure(null);
    try {
      await preview(survivorId, duplicateIds);
      if (previewAttempt.current === attempt) setIsPreviewReady(true);
    } catch (error) {
      if (previewAttempt.current === attempt) {
        setIsPreviewReady(false);
        setFailure(`Could not preview this merge. ${toUserMessage(error)}`);
      }
    }
  }, [duplicateIds, preview, survivorId]);

  const merge = useCallback(async () => {
    if (survivorId === null || duplicateIds.length === 0) return;
    setFailure(null);
    try {
      const record = await runMerge(survivorId, duplicateIds);
      setMergedRecordId(record.id);
      setSearchSyncPending(record.searchSyncPending);
      notify.success(`${duplicateIds.length} duplicate${duplicateIds.length === 1 ? '' : 's'} merged`);
      onChanged();
    } catch (error) {
      setFailure(`Could not merge these records. ${toUserMessage(error)}`);
    }
  }, [duplicateIds, onChanged, runMerge, survivorId]);

  const undo = useCallback(async () => {
    if (mergedRecordId === null) return;
    setFailure(null);
    try {
      const record = await runUndo(mergedRecordId);
      notify.success('Merge undone');
      onChanged();
      await findForRole(role);
      setSearchSyncPending(record.searchSyncPending);
    } catch (error) {
      setFailure(`Could not undo this merge. ${toUserMessage(error)}`);
    }
  }, [findForRole, mergedRecordId, onChanged, role, runUndo]);

  return (
    <AgModal open={open} onClose={onClose} title="Find duplicate business entities" size="lg">
      <AgModalContent>
        <div className="flex flex-col gap-5">
          <div className="flex gap-2" aria-label="Entity type">
            <AgButton variant={role === 'CUSTOMER' ? 'selected' : 'white'} onPress={() => void findForRole('CUSTOMER')}>
              Customers
            </AgButton>
            <AgButton variant={role === 'VENDOR' ? 'selected' : 'white'} onPress={() => void findForRole('VENDOR')}>
              Vendors
            </AgButton>
          </div>

          {finding && <AgText color="secondary">Looking for likely duplicates…</AgText>}
          {failure && (
            <div role="alert" className="rounded-lg border border-red-6 bg-red-2 p-4">
              <AgText>{failure}</AgText>
            </div>
          )}
          {!failure && !finding && groups.length === 0 && (
            <div className="rounded-lg border border-neutral-5 p-4">
              <AgText>No likely {role === 'CUSTOMER' ? 'customer' : 'vendor'} duplicates found.</AgText>
            </div>
          )}

          {groups.length > 1 && (
            <div className="flex flex-wrap gap-2" aria-label="Duplicate groups">
              {groups.map((group) => (
                <AgButton
                  key={group.key}
                  variant={selectedGroup?.key === group.key ? 'selected' : 'white'}
                  onPress={() => selectGroup(group.key)}
                >
                  {group.members[0]?.entity.name} · {group.members.length} records
                </AgButton>
              ))}
            </div>
          )}

          {selectedGroup && mergedRecordId === null && (
            <section className="rounded-lg border border-neutral-5 p-4" aria-label="Duplicate group">
              <AgText size="14" weight="medium">
                Choose the record to keep
              </AgText>
              <AgText size="12" color="secondary" className="mt-1 block">
                Grouped because of {selectedGroup.matchedOn.map((match) => matchLabels[match]).join(' and ')}
              </AgText>
              <div className="mt-3 flex flex-col gap-2">
                {selectedGroup.members.map((member) => (
                  <label
                    key={member.entity.id}
                    className="flex cursor-pointer items-start gap-3 rounded-md border border-neutral-4 p-3"
                  >
                    <input
                      type="radio"
                      name="business-entity-survivor"
                      checked={survivorId === member.entity.id}
                      onChange={() => selectSurvivor(member.entity.id)}
                      aria-label={`Keep ${member.entity.name}`}
                    />
                    <span className="flex flex-col">
                      <AgText weight="medium">
                        {member.entity.name}
                        {member.entity.bluebookId ? ` · Blue Book ${member.entity.bluebookId}` : ''}
                      </AgText>
                      <AgText size="12" color="secondary">
                        {memberFacts(member)}
                      </AgText>
                    </span>
                  </label>
                ))}
              </div>
            </section>
          )}

          {isPreviewReady && previewResult && mergedRecordId === null && (
            <section className="rounded-lg bg-neutral-2 p-4" aria-label="Merge preview">
              <AgText size="14" weight="medium">
                Records that will move to the survivor
              </AgText>
              <div className="mt-2 flex flex-wrap gap-2">
                {previewSummary.headline.length === 0 ? (
                  <AgText size="13" color="secondary">
                    No linked records
                  </AgText>
                ) : (
                  previewSummary.headline.map(({ label, count }) => (
                    <span key={label} className="rounded-full bg-white px-3 py-1 text-13 text-neutral-text">
                      {count} {label}
                    </span>
                  ))
                )}
              </div>
              {previewSummary.alsoCarried.length > 0 && (
                <AgText size="12" color="secondary" className="mt-2 block">
                  Also carried over:{' '}
                  {previewSummary.alsoCarried.map(({ label, count }) => `${count} ${label}`).join(' · ')}
                </AgText>
              )}
            </section>
          )}

          {mergedRecordId !== null && (
            <div className="rounded-lg border border-green-6 bg-green-2 p-4">
              <AgText weight="medium">Merge complete</AgText>
              <AgText size="13" color="secondary" className="mt-1 block">
                Linked records now point to the survivor. You can undo this merge from here.
              </AgText>
            </div>
          )}

          {searchSyncPending && (
            <div role="status" className="rounded-lg border border-yellow-6 bg-yellow-2 p-4">
              <AgText>Changes were saved, but search is still catching up. Results may be briefly out of date.</AgText>
            </div>
          )}
        </div>
      </AgModalContent>
      <AgModalActions>
        {mergedRecordId !== null ? (
          <AgButton variant="white" isDisabled={undoing} onPress={() => void undo()}>
            Undo merge
          </AgButton>
        ) : isPreviewReady ? (
          <AgButton variant="primary" isDisabled={merging} onPress={() => void merge()}>
            Merge {duplicateIds.length} duplicate{duplicateIds.length === 1 ? '' : 's'}
          </AgButton>
        ) : (
          <AgButton
            variant="primary"
            isDisabled={survivorId === null || previewing}
            onPress={() => void previewMerge()}
          >
            Preview merge
          </AgButton>
        )}
      </AgModalActions>
    </AgModal>
  );
}
